// FLEET · ROUTES (v2 slot 4 of `v2-the-deep.md` §18.2, R1811; mockup round
// 088 card 09; R2589): the course plots of the empire.
//
// The pill stands only while the flag `routes` holds (`FLAG_SUBS`). Its
// body lists the saved course plots of `Empire.plots` in the order the
// commander saved them, one 44 px row each: the route mark, the name, the
// legs, a loop and the length, and three presses. SEND sends the plot to
// the own fleet the sheet names, as the legs of one course (a plot keeps
// no leg 0, so the hub writes the position of the fleet). One fleet per
// press (R1307). RENAME opens the name card, and ✕ deletes the plot after
// a second press (R696). SAVE AS PLOT of the raised plot line adds a row.
//
// Under the flag `routes` (v2-the-deep.md §7.2, R2646) the page opens with
// the shipping lanes of the faction: the own plots with the LANE switch
// on, then the lanes of the faction mates (`Empire.lanes`) with the name
// of their commander and SEND. Each own plot row carries the LANE switch,
// lit while the plot is a lane; it is off while the plot has fewer legs
// than a lane needs (`CatalogRules.route_lane_legs_min`, R3067), the
// commander keeps the cap of lanes (`CatalogRules.route_lanes_max`), or a
// leg crosses a ridge (R2730). The hub refuses a lane over a ridge with
// its own walk of every cell the line crosses, which has the last word.
// The course plots that are no lane follow.
//
// Each lane row carries VIEW (R2709): the map fits the lane, the lane
// stands out on the chart for a short time, and its lane card opens.
//
// The lanes of one road (v2-the-deep.md §7.2 rule 12, R3074) stand
// together under a head with the road and its commanders, at the place of
// the first lane of the road. The sub line of a quiet lane (rule 13) says
// so.
//
// A row of the page wears the class `rt` (R2709): the presses stand on a
// line of their own under the name, so the name takes the whole width of
// the row and wraps where it is longer, and is never cut.
//
// The page is pure: it reads the state and writes markup.

import type { CoursePlot } from '../../gen/proto/aquaphobia';
import { card as CS, overlay as S } from '../../gen/strings';
import { type GameState, num } from '../../state';
import { BLOCK_RIDGE, HEX_SIZE, cellAt, mpxToPx } from '../../world';
import { buttonHtml, type CardEnv } from '../cards/card';
import { esc } from '../esc';
import { noteRowHtml } from '../fleetlist';
import { loopParts, type PlotLeg } from '../plotline';

/** What FLEET · ROUTES reads beside the plots. */
export interface RoutesCtx {
  plots: readonly CoursePlot[];
  /** The own fleet the sheet names, or null: SEND sends to it. */
  fleet: { id: number; name: string } | null;
  env: CardEnv;
  /** The overlay's arm gate (R696): ✕ draws its armed face. */
  armed: (key: string) => boolean;
  /** The shipping lanes (R2646): the flag `routes` holds, the lanes of
   *  the faction mates, the cap of the own lanes, the legs a lane holds
   *  at least (R3067), and the terrain bytes of `Hello.blocked` that the
   *  ridge test reads (R2730). */
  lanes?: { mates: readonly CoursePlot[]; max: number; minLegs: number; blocked?: Uint8Array | null };
}

/** The legs of a plot in world px, as the plot line keeps them. */
export function plotLegs(p: CoursePlot): PlotLeg[] {
  return p.legs.map((w) => ({ x: mpxToPx(w.xMpx), y: mpxToPx(w.yMpx), loop: w.loop, passes: w.passes }));
}

/** The length between the legs of a plot, in world px, with the leg back
 *  of each loop part. The plot starts at its first leg, so the joining run
 *  from the fleet does not count (R2729). */
export function plotSpanPx(legs: readonly PlotLeg[]): number {
  let len = 0;
  for (let i = 1; i < legs.length; i++) len += Math.hypot(legs[i].x - legs[i - 1].x, legs[i].y - legs[i - 1].y);
  for (const p of loopParts(legs)) {
    const a = legs[p.last], b = legs[p.open];
    len += Math.hypot(a.x - b.x, a.y - b.y);
  }
  return len;
}

/** The lanes of the faction as the reader holds them: the own plots that
 *  are lanes (commander 0), then the lanes of the faction mates. */
export function factionLanes(s: Pick<GameState, 'plots' | 'factionLanes'>): CoursePlot[] {
  return [...s.plots.filter((p) => p.lane), ...s.factionLanes];
}

/** The road of a lane (rule 12): its name with the road word, its length
 *  (the lengths of its lanes) and the count of its commanders; null for a
 *  lane of no road. */
export function laneRoad(
  s: Pick<GameState, 'plots' | 'factionLanes'>, p: CoursePlot,
): { name: string; px: number; commanders: number } | null {
  if (p.road === 0) return null;
  const lanes = factionLanes(s).filter((x) => x.road === p.road);
  return {
    name: CS.lane_road({ name: p.roadName }),
    px: lanes.reduce((sum, x) => sum + plotSpanPx(plotLegs(x)), 0),
    commanders: new Set(lanes.map((x) => x.owner)).size,
  };
}

/** Does a leg of a plot, or the leg back of a loop part that opens at a
 *  leg, cross a ridge cell of `blocked` (R2730)? The walk reads each
 *  quarter hex along each leg, so it can miss a corner that the leg only
 *  clips; the hub then refuses the switch. */
export function plotOverRidge(legs: readonly PlotLeg[], blocked: Uint8Array): boolean {
  const rock = (a: PlotLeg, b: PlotLeg): boolean => {
    const n = Math.floor(Math.hypot(b.x - a.x, b.y - a.y) / (HEX_SIZE / 4)) + 1;
    for (let i = 0; i <= n; i++) {
      const c = cellAt(a.x + (b.x - a.x) * i / n, a.y + (b.y - a.y) * i / n);
      if (c >= 0 && blocked[c] === BLOCK_RIDGE) return true;
    }
    return false;
  };
  for (let i = 1; i < legs.length; i++) if (rock(legs[i - 1], legs[i])) return true;
  return loopParts(legs).some((p) => p.open >= 0 && rock(legs[p.last], legs[p.open]));
}

/** The sub line of a plot: the legs, the loop parts and the length, after
 *  the quiet mark of a quiet lane (R3074). */
function plotSub(p: CoursePlot): string {
  const legs = plotLegs(p);
  const loops = loopParts(legs).length;
  return [
    ...(p.lane && p.quiet ? [S.routes_lane_quiet] : []),
    S.routes_legs({ n: legs.length }),
    ...(loops > 0 ? [S.routes_loops({ n: loops })] : []),
    S.routes_px({ px: num(Math.round(plotSpanPx(legs))) }),
  ].join(' · ');
}

/** SEND of a row: the press sends the own fleet the sheet names. */
function sendPress(act: string, id: number, n: number | undefined, ctx: RoutesCtx): string {
  return buttonHtml({
    act, id, n, face: 'dark', label: S.routes_send, off: ctx.fleet === null,
    title: ctx.fleet ? S.routes_send_title({ name: ctx.fleet.name }) : S.routes_send_none,
  }, ctx.env);
}

/** VIEW of a lane row (R2709): the plot `id` of the commander `owner`, 0
 *  for an own plot. */
function viewPress(id: number, owner: number, ctx: RoutesCtx): string {
  return buttonHtml({ act: 'laneview', id, n: owner, face: 'quiet', label: S.routes_view, title: S.routes_view_title }, ctx.env);
}

/** The LANE switch of an own plot (R2646): lit while the plot is a lane,
 *  off while the plot has fewer legs than a lane needs (R3067), the cap of
 *  lanes is full or a leg crosses a ridge (R2730). A lane of fewer legs
 *  that the snapshot holds stays lit, so its commander can clear it. */
function laneSwitch(p: CoursePlot, ctx: RoutesCtx): string {
  if (!ctx.lanes) return '';
  const held = ctx.plots.filter((x) => x.lane).length;
  const short = p.legs.length < ctx.lanes.minLegs;
  const full = !p.lane && held >= ctx.lanes.max;
  const blocked = ctx.lanes.blocked;
  const ridge = !p.lane && !short && !!blocked && plotOverRidge(plotLegs(p), blocked);
  const title = p.lane ? S.routes_lane_on_title
    : short ? S.routes_lane_short({ n: ctx.lanes.minLegs })
      : ridge ? S.routes_lane_ridge
        : full ? S.routes_lane_full({ n: ctx.lanes.max })
          : S.routes_lane_off_title;
  return buttonHtml({
    act: 'plotlane', id: p.id, face: 'quiet', label: S.routes_lane, on: p.lane, off: !p.lane && (short || ridge || full), title,
  }, ctx.env);
}

/** One row of a course plot. */
function plotRow(p: CoursePlot, ctx: RoutesCtx): string {
  const key = `plotdel:${p.id}`;
  const press = sendPress('plotsend', p.id, undefined, ctx)
    + (p.lane && ctx.lanes ? viewPress(p.id, 0, ctx) : '')
    + laneSwitch(p, ctx)
    + buttonHtml({ act: 'plotname', id: p.id, face: 'quiet', label: S.routes_rename }, ctx.env)
    + buttonHtml({
      act: 'plotdel', id: p.id, face: 'quiet', label: S.routes_delete, title: S.routes_delete_title,
      arm: true, armLabel: S.routes_delete_armed, armed: ctx.armed(key), danger: true,
    }, ctx.env);
  return noteRowHtml(p.name, p.lane && ctx.lanes ? S.routes_lane_mark : CS.plot_mark, plotSub(p), press, 'rt');
}

/** One row of a lane of a faction mate (R2646): the name of its
 *  commander, and SEND, which sends the named own fleet on its legs. */
function mateRow(p: CoursePlot, ctx: RoutesCtx): string {
  const sub = `${esc(S.routes_lane_by({ name: p.ownerName }))} · ${plotSub(p)}`;
  return noteRowHtml(p.name, S.routes_lane_mark, sub,
    sendPress('lanesend', p.id, p.owner, ctx) + viewPress(p.id, p.owner, ctx), 'rt');
}

/** The lane rows in road order (R3074): each lane of no road at its
 *  place, and the lanes of one road together under the head of the road at
 *  the place of its first lane. */
function laneRows(own: readonly CoursePlot[], mates: readonly CoursePlot[], ctx: RoutesCtx): string {
  const all = [...own, ...mates];
  const row = (p: CoursePlot): string => (p.owner === 0 ? plotRow(p, ctx) : mateRow(p, ctx));
  const done = new Set<number>();
  let html = '';
  for (const p of all) {
    if (p.road === 0) { html += row(p); continue; }
    if (done.has(p.road)) continue;
    done.add(p.road);
    const road = laneRoad({ plots: own, factionLanes: mates }, p);
    if (road) {
      html += `<h3>${esc(S.routes_road_head({ road: road.name, who: CS.lane_commanders({ n: road.commanders }) }))}</h3>`;
    }
    html += all.filter((x) => x.road === p.road).map(row).join('');
  }
  return html;
}

/** The page of FLEET · ROUTES. */
export function routesHtml(ctx: RoutesCtx): string {
  const own = ctx.lanes ? ctx.plots.filter((p) => p.lane) : [];
  const mates = ctx.lanes?.mates ?? [];
  const rest = ctx.lanes ? ctx.plots.filter((p) => !p.lane) : ctx.plots;
  if (ctx.plots.length === 0 && mates.length === 0) return `<div class="empty">${S.routes_empty}</div>`;
  const lanes = own.length + mates.length > 0
    ? `<h3>${S.routes_lanes_head({ n: own.length + mates.length })}</h3>`
      + laneRows(own, mates, ctx)
      + `<div class="empty">${esc(S.routes_lanes_hint)}</div>`
    : '';
  const plots = rest.length > 0
    ? `<h3>${S.routes_head({ n: rest.length })}</h3>` + rest.map((p) => plotRow(p, ctx)).join('')
    : '';
  return lanes + plots
    + `<div class="empty">${esc(ctx.fleet ? S.routes_hint_send({ name: ctx.fleet.name }) : S.routes_hint_none)}</div>`;
}
